import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Icon, makeStyles, Typography } from '@popmenu/common-ui';
import { Heart } from '@popmenu/web-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import type { RootPlaceBySlugQuery_rootPlace_RootPlace_menuItems_MenuItemPaginationHandler_records_MenuItem as DishType } from '../../../libs/gql/queries/root_places/rootPlaceBySlugQuery.generated';
import { formatNumber } from '../../../utils/numbers';
import CustomImg from '../../../shared/CustomImg';

type DishFeedCardProps = DishType & {
  dishPathPrefix: string
};

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
  },
  linkOverlay: {
    borderRadius: theme.spacing(1),
    height: '100%',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    textIndent: '-999em',
    top: 0,
    width: '100%',
  },
  nameContainer: {
    overflow: 'auto',
  },
  photo: {
    borderRadius: theme.spacing(1),
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  photoContainer: {
    backgroundColor: theme.palette.gray.main,
    borderRadius: theme.spacing(1),
    height: 0,
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    paddingTop: '75%',
    position: 'relative',
  },
  popsContainer: {
    float: 'right',
    marginLeft: theme.spacing(1),
  },
  restaurantName: {
    fontSize: '0.75rem',
  },
}));

export const DishFeedCard = ({ dishPathPrefix, ...dish }: DishFeedCardProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const dishPath = `${dishPathPrefix}/dishes/${dish.idAsSlug}`;
  return (
    <Box className={classes.container} component="section">
      <Box className={classes.photoContainer}>
        {dish.featuredPhoto?.photoUrl && (
          <CustomImg
            alt={dish.displayName}
            className={classes.photo}
            size="sm"
            src={dish.featuredPhoto.photoUrl}
          />
        )}
      </Box>
      <Box className={classes.nameContainer}>
        <Typography className={classes.popsContainer}>
          <Icon aria-label={intl.formatMessage({ defaultMessage: 'Likes' })} icon={Heart} />
          {formatNumber(dish.popsCount)}
        </Typography>
        <Typography component="h2">
          {dish.displayName}
        </Typography>
        {dish.restaurant && dish.location && (
          <Typography className={classes.restaurantName}>
            <FormattedMessage
              defaultMessage="{restaurantName} in {city}, {stateOrCountry}"
              values={{
                city: dish.location.city,
                restaurantName: dish.restaurant.name,
                stateOrCountry: dish.location.state || dish.location.countryName,
              }}
            />
          </Typography>
        )}
      </Box>
      <Link
        className={classes.linkOverlay}
        data-cy="dish-feed-card-overlay-link"
        to={dishPath}
      >
        {dish.displayName}
      </Link>
    </Box>
  );
};
